import { AlertTriangle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';
import { Button } from '@/shared/ui/button';
import { currency, type CurrencyCode } from '@/shared/lib/format';
import { quantityLabel } from '@/shared/lib/passbook-utils';
import type { Passbook } from '@/shared/types';
import type { OperatorPreviewResult } from '../api/operator-preview';

interface OperatorConfirmSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  passbook: Passbook;
  preview: OperatorPreviewResult | null;
  /** Falha do simulate — bloqueia confirmação. */
  previewError?: string | null;
  redemptionOn: boolean;
  currencyCode: CurrencyCode;
  purchaseCode?: string;
  redemptionProductName?: string | null;
  redemptionRan?: boolean;
  confirming?: boolean;
  partialError?: string | null;
  onConfirm: () => void;
  onRetryAward?: () => void;
  retrying?: boolean;
}

export function OperatorConfirmSheet({
  open,
  onOpenChange,
  passbook,
  preview,
  previewError,
  redemptionOn,
  currencyCode,
  purchaseCode,
  redemptionProductName,
  redemptionRan,
  confirming,
  partialError,
  onConfirm,
  onRetryAward,
  retrying,
}: OperatorConfirmSheetProps) {
  const label = quantityLabel(passbook);
  const fmt = (n: number) => currency(n, currencyCode);
  const showPartial = Boolean(partialError && redemptionRan);
  const redemptionLine =
    redemptionOn && redemptionProductName ? `Produto: ${redemptionProductName}` : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full max-w-md flex-col sm:w-full">
        <SheetHeader>
          <SheetTitle>Confirmar conta</SheetTitle>
          <SheetDescription>
            {redemptionOn
              ? 'Resgate e lançamento serão aplicados na mesma ação.'
              : 'Somente o lançamento da compra será registrado.'}
          </SheetDescription>
        </SheetHeader>

        {previewError ? (
          <div className="glass-thin mb-4 flex gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="space-y-1">
              <p className="font-medium text-destructive">{previewError}</p>
              <p className="text-muted-foreground">
                A prévia da regra falhou. Ajuste o valor da compra ou os benefícios.
              </p>
            </div>
          </div>
        ) : null}

        {showPartial ? (
          <div className="glass-thin mb-4 flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div className="space-y-2">
              <p className="font-medium">Resgate ok, lançamento falhou</p>
              <p className="text-muted-foreground">{partialError}</p>
              <Button type="button" size="sm" className="h-11" loading={retrying} onClick={onRetryAward}>
                Tentar lançar de novo
              </Button>
            </div>
          </div>
        ) : null}

        {preview ? (
          <div className="glass space-y-2 rounded-2xl p-4 text-sm">
            {purchaseCode ? (
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Código da compra</span>
                <span className="truncate font-mono text-xs">{purchaseCode}</span>
              </div>
            ) : null}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Compra</span>
              <span className="tabular-nums">{fmt(preview.purchaseValue)}</span>
            </div>
            {preview.discount > 0 ? (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Desconto</span>
                <span className="tabular-nums">−{fmt(preview.discount)}</span>
              </div>
            ) : null}
            {redemptionLine ? (
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Resgate</span>
                <span className="truncate text-right font-medium">{redemptionLine}</span>
              </div>
            ) : null}
            <div className="flex justify-between border-t border-[hsl(var(--glass-border)/0.4)] pt-2 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{fmt(preview.netValue)}</span>
            </div>
            {preview.awardUnits > 0 ? (
              <div className="flex justify-between pt-1">
                <span className="text-muted-foreground">Cliente ganha</span>
                <span className="tabular-nums font-medium">
                  {preview.program === 'cashback'
                    ? fmt(preview.cashbackEarned)
                    : preview.program === 'points'
                      ? `${preview.pointsEarned} pts`
                      : `${preview.stampsEarned || preview.awardUnits} ${label.toLowerCase()}`}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-auto flex flex-col gap-2 pt-6">
          {!showPartial ? (
            <Button
              type="button"
              className="h-12 w-full text-base"
              loading={confirming}
              disabled={!preview || Boolean(previewError) || confirming}
              onClick={onConfirm}
            >
              Confirmar
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            className="h-11 w-full"
            onClick={() => onOpenChange(false)}
            disabled={confirming || retrying}
          >
            {showPartial ? 'Fechar' : 'Cancelar'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
